import React, { useState } from 'react'
import {
  useApiRequest,
  useApiToken,
  useError,
  useForm
} from '../../hooks'

import TextField from '../Form/TextField'
import FileField from '../Form/FileField'
import Button from '../Button'
import ProgressBar from '../ProgressBar'
import Columns from '../Columns'
import Spinner from '../Spinner'

import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage'
import { getFirestore, collection, addDoc } from 'firebase/firestore'
import { auth, db } from '../../firebase'

export default function ({ incidentId, onAttachmentCreated }) {
  const accessToken = useApiToken()
  const [incident] = useApiRequest(`incidents/${incidentId}`, null)
  const [uploadProgress, setUploadProgress] = useState(null)
  const [loading, setLoading] = useState(false)
  const setError = useError()
  const [getFieldProps, formData, formError] = useForm()

  if (!incident) {
    return <Spinner />
  }

  // ✅ Calculate SHA-256 hash using Web Crypto API (browser-native)
  async function calculateSHA256(file) {
    const arrayBuffer = await file.arrayBuffer()
    const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
    return hashHex
  }

  async function handleSubmit(e) {
    e.preventDefault()

    const { name, file } = formData

    if (!file) {
      formError({ file: 'Please select a file.' })
      return
    }

    setLoading(true)

    try {
      const storage = getStorage()
      const path = `stage/incidents/${incidentId}/${file.name}`
      const storageRef = ref(storage, path)

      const uploadTask = uploadBytesResumable(storageRef, file)

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = snapshot.bytesTransferred / snapshot.totalBytes
          setUploadProgress(progress)
        },
        (error) => {
          setError(error)
          setLoading(false)
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref)
          const sha256 = await calculateSHA256(file)

          const attachment = {
            name,
            fileName: file.name,
            path,
            sha256,
            uploadedAt: new Date().toISOString(),
            uploadedBy: auth.currentUser?.uid || 'unknown',
            resource: {
              url: downloadURL,
              mimeType: file.type,
            },
          }

          // ✅ Store metadata in Firestore
          await addDoc(collection(db, 'incidents', incidentId, 'attachments'), attachment)

          onAttachmentCreated && onAttachmentCreated(attachment)
          setLoading(false)
        }
      )
    } catch (err) {
      console.error(err)
      setLoading(false)
      setError(err)
    }
  }

  return (
    <div className='box'>
      <form onSubmit={handleSubmit}>
        <Columns slice={2}>
          <TextField label='Incident' value={incident?.description?.stringValue} readOnly={true} />
          <TextField
            label='Name'
            name='name'
            placeholder='Name'
            required={true}
            {...getFieldProps()}
          />
          <FileField label='File' name='file' {...getFieldProps()} />
        </Columns>
        {uploadProgress && (
          <ProgressBar
            value={uploadProgress}
            className='is-info'
            style={{ marginTop: '2em' }}
          />
        )}
        <div className='level'>
          <div className='level-left' />
          <div className='level-right'>
            <div className='level-item'>
              <Button
                type='submit'
                className={loading ? 'is-loading' : ''}
                enabled={`${!loading}`}>
                Submit attachment
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}
